import type { SelectOption } from "../select/select.tsx";

export type TimePeriodUnit = "day" | "month";

export type TimePeriodValue = {
  amount: number;
  unit: TimePeriodUnit;
};

export function getTimePeriodUnitOptions(amount: number): SelectOption[] {
  const dayLabel = amount === 1 ? "Day" : "Days";
  const monthLabel = amount === 1 ? "Month" : "Months";

  return [
    { value: "day", label: dayLabel },
    { value: "month", label: monthLabel },
  ];
}

export interface ClampTimePeriodOptions {
  minAmount?: number;
  maxDays?: number;
  maxMonths?: number;
}

export function clampTimePeriodAmount(
  amount: number,
  unit: TimePeriodUnit,
  {
    minAmount = 1,
    maxDays = 365,
    maxMonths = 12,
  }: ClampTimePeriodOptions = {},
): number {
  const max = unit === "day" ? maxDays : maxMonths;
  const floored = Math.floor(amount);

  if (!Number.isFinite(floored)) {
    return minAmount;
  }

  return Math.min(max, Math.max(minAmount, floored));
}

export function timePeriodToDays(amount: number, unit: TimePeriodUnit): number {
  const floored = Math.floor(amount);
  return unit === "month" ? floored * 30 : floored;
}

/** Strip non-digits for text-based amount entry. */
export function digitsOnlyAmountInput(raw: string): string {
  return raw.replace(/\D/g, "");
}

/** Amount used for Day/Days unit labels while the text field is empty or partial. */
export function amountForUnitLabels(amountText: string, minAmount = 1): number {
  if (amountText === "") {
    return minAmount;
  }
  const parsed = parseInt(amountText, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return minAmount;
  }
  return parsed;
}
